import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';
import { DESKTOP } from '@/constants';
import { getAllShields } from '@/helpers';
import {
  setTextToSelectedSection,
  handleEndlessTextInput,
  handleKeyboardInputFocus,
  handleKeyboardInputUnfocus
} from '@/actions';
import FloatableSectionContentTextarea from './FloatableSectionContentTextarea.jsx';
import EditableSectionContentTextarea from './EditableSectionContentTextarea.jsx';

const FloatableSectionContentTextareaContainer = (props) => {
  const { targetDevice, customOnFocus, customOnBlur, systemOnFocus, systemOnBlur, systemOnChange, ...restProps } = props;
  // maybe bad architecture solution and better to use 'useCallback' because custom callbacks can be changed with other context
  // but if we know where this component uses - it can be fine
  const clbsRef = useRef({
    onFocus: () => { customOnFocus(); systemOnFocus(); },
    onBlur: () => { customOnBlur(); systemOnBlur(); }
  });
  const isDesktop = targetDevice === DESKTOP;

  return isDesktop ? (
    <EditableSectionContentTextarea {...restProps} onChange={systemOnChange} {...clbsRef.current} />
  ) : (
    <FloatableSectionContentTextarea {...restProps} onChange={systemOnChange} {...clbsRef.current} />
  );
};

const mapStateToProps = state => ({
  targetDevice: state.core.targetDevice,
  pages: state.core.pages,
  selectedPage: state.core.pages[state.core.pagesSelection[0]],
  sectionsSelection: state.core.sectionsSelection,
  areSectionsHidden: state.core.areSectionsHidden,
  scaleCoef: state.core.scaleCoef,
  fictiveSections: state.core.fictiveSections,
  fictiveSectionsSelection: state.core.fictiveSectionsSelection,
  fontSizeCoef: state.core.fontSizeCoef,
  lineHeightCoef: state.core.lineHeightCoef,
  floatableInputVisible: state.core.floatableInputVisible,
});

const mapDispatchToProps = dispatch => ({
  onChange: text => dispatch(setTextToSelectedSection(text, false, false)),
  onFocus: () => dispatch(handleKeyboardInputFocus()),
  onBlur: () => dispatch(handleKeyboardInputUnfocus()),
  handleEndlessTextInput: (sectionId) => dispatch(handleEndlessTextInput(sectionId, false)),
  handleTextInputBlur: (text) => dispatch(setTextToSelectedSection(text))
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { floatableInputVisible, targetDevice, pages, selectedPage, sectionsSelection, areSectionsHidden, scaleCoef, fontSizeCoef, lineHeightCoef } = stateProps;
  let selectedSection = null;
  for (const secId in sectionsSelection) {
    const { section } = getAllLayers(secId, pages);
    selectedSection = section;
    break;
  }

  let selectedShield = null;
  const { layouts } = selectedPage;
  for (let layoutId in layouts) {
    const { groups } = layouts[layoutId];
    for (let groupId in groups) {
      const { shields } = groups[groupId];
      for (let shId in shields) {
        const shield = shields[shId];
        if (shield.id === selectedSection?.shieldId) {
          selectedShield = shield;
          break;
        }
      }
    }
  }

  let transformStr = '';
  if (selectedSection?.mirrorX) {
    transformStr = `${transformStr} scale(-1, 1)`
  }
  if (selectedSection?.mirrorY) {
    transformStr = `${transformStr} scale(1, -1)`
  }

  const whiteSpace = selectedPage?.resizeType === '@/ENDLESS_FIXED' ? 'normal' : 'pre';

  const sectionAreaSize = selectedSection? {
    whiteSpace: whiteSpace,
    height: selectedSection.height,
    width: selectedSection.width,
    maxHeight: selectedSection.height,
    maxWidth: selectedSection.width,
    fontFamily: (selectedSection.font),
    fontStyle: (selectedSection.isItalic? 'italic' : ''),
    textDecoration: (selectedSection.isUnderline? 'underline' : ''),
    fontSize: (selectedSection.fontSize * scaleCoef) * (fontSizeCoef[selectedSection.font] || 1),
    lineHeight: lineHeightCoef,
    fontWeight: (selectedSection.isBold? 'bold' : 'normal'),
    color: (selectedSection.fontColor),
    textAlign: (selectedSection.horizontalAlignment),
    alignItems: selectedSection.verticalAlignment.replace('middle', 'center').replace('bottom','end').replace('top','start'),
    transform: transformStr,
    angle: selectedSection.angle,
    overflow: 'hidden',
    border: 'none',
    outline: 'none',
    resize: 'none',
    padding: 0,
    rows: 1
  } : {
    height: 20,
    width: 20
  }

  return {
    targetDevice,
    customOnFocus: ownProps.onFocus,
    customOnBlur: ownProps.onBlur,
    systemOnFocus: dispatchProps.onFocus,
    sectionAreaSize: sectionAreaSize,
    systemOnBlur: () => {
      dispatchProps.onBlur();
      // fix for endless material for case when length based on content;
      // it does'nt work as with 'mobile' input, because now content doesn't render, and calculation bases on content
      // so we need to check size after content render
      const shieldsNumber = Object.keys(getAllShields(selectedPage)).length;
      if (!selectedShield?.isMatrix && shieldsNumber === 1) {
        setTimeout(() => {
          dispatchProps.handleEndlessTextInput(selectedSection.id)
        }, 0);
      }
    },
    systemOnChange: dispatchProps.onChange,
    visible: floatableInputVisible,
    text: selectedSection?.text || '',
    selectedSection: selectedSection,
    selectedPage,
    scaleCoef,
    inputOnType: dispatchProps.handleTextInputBlur,
    targetElementSelector: areSectionsHidden ? `#${selectedSection.shieldId}` : `#${selectedSection.id}-group`,
    lineHeightCoef
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(FloatableSectionContentTextareaContainer);
